import { isStandalone } from './config.js';

export type RouteParams = Record<string, string>;
export type RouteHandler = (params: RouteParams) => void | (() => void) | Promise<void | (() => void)>;

interface Route {
  regex: RegExp;
  paramNames: string[];
  handler: RouteHandler;
}

const routes: Route[] = [];
let fallbackHandler: RouteHandler | null = null;
let currentCleanup: (() => void) | null = null;

export function addRoute(pattern: string, handler: RouteHandler): void {
  const paramNames: string[] = [];
  const regexStr = pattern.replace(/:(\w+)/g, (_, name: string) => {
    paramNames.push(name);
    return '([^/]+)';
  });
  routes.push({
    regex: new RegExp(`^${regexStr}$`),
    paramNames,
    handler,
  });
}

export function setFallback(handler: RouteHandler): void {
  fallbackHandler = handler;
}

export function navigate(path: string): void {
  if (isStandalone()) {
    window.location.hash = '#' + path;
  } else {
    history.pushState({}, '', path);
    resolve();
  }
}

export function currentPath(): string {
  if (isStandalone()) {
    const hash = window.location.hash;
    return hash.startsWith('#') ? hash.slice(1) : '/graph';
  }
  return window.location.pathname;
}

async function resolve(): Promise<void> {
  if (currentCleanup) {
    currentCleanup();
    currentCleanup = null;
  }

  const path = currentPath();

  if (path === '/' || path === '') {
    if (isStandalone()) {
      window.location.hash = '#/graph';
    } else {
      history.replaceState({}, '', '/graph');
    }
    return resolve();
  }

  let handler = fallbackHandler;
  const params: RouteParams = {};

  for (const route of routes) {
    const match = path.match(route.regex);
    if (match) {
      route.paramNames.forEach((name, i) => {
        params[name] = decodeURIComponent(match[i + 1]);
      });
      handler = route.handler;
      break;
    }
  }

  if (handler) {
    const cleanup = await handler(params);
    if (typeof cleanup === 'function') {
      currentCleanup = cleanup;
    }
  }

  window.scrollTo(0, 0);
  window.dispatchEvent(new Event('routechange'));
}

export function start(): void {
  if (isStandalone()) {
    window.addEventListener('hashchange', () => resolve());
  } else {
    window.addEventListener('popstate', () => resolve());
  }
  resolve();
}
